import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PageHeader from "../../../component/PageHeader";
import Footer from "../../../component/Footer";
import { apiFetch } from "../../../component/utils/ApiFetch";
import {
  RESEND_COOLDOWN_MS,
  VERIFICATION_LINK_TTL_MINUTES,
  clearCooldownUntil,
  formatCooldown,
  getRemainingSeconds,
  readCooldownUntil,
  writeCooldownUntil,
} from "./resendCooldown";
import "../Account.css";

export default function CheckEmail() {
  const location = useLocation();
  const emailFromState = location.state?.email || "";
  const sentFromState = location.state?.sent;

  const email = String(emailFromState || "").trim();
  const [status, setStatus] = useState(
    sentFromState === false ? "Email failed to send. Please resend." : ""
  );
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(() => readCooldownUntil(emailFromState));

  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const existing = readCooldownUntil(email);
    if (existing > Date.now()) {
      setCooldownUntil(existing);
      return;
    }

    clearCooldownUntil(email);

    if (sentFromState === true) {
      const nextCooldownUntil = Date.now() + RESEND_COOLDOWN_MS;
      writeCooldownUntil(email, nextCooldownUntil);
      setCooldownUntil(nextCooldownUntil);
    } else {
      setCooldownUntil(0);
    }
  }, [email, sentFromState]);

  const emailOk = useMemo(() => {
    if (!email) return false;
    if (email.length > 254) return false;
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }, [email]);

  const remainingSeconds = useMemo(() => getRemainingSeconds(cooldownUntil), [cooldownUntil]);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    if (cooldownUntil <= Date.now()) {
      clearCooldownUntil(email);
      setCooldownUntil(0);
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (cooldownUntil <= Date.now()) {
        clearCooldownUntil(email);
        setCooldownUntil(0);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [cooldownUntil, email]);

  const resendDisabled = sending || !emailOk || remainingSeconds > 0;

  async function handleResend() {
    setError("");
    setStatus("");

    if (!emailOk) {
      setError("Enter a valid email address.");
      return;
    }

    setSending(true);
    try {
      const res = await apiFetch("/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      const data = await res.json().catch(() => ({}));
      console.info("[check-email] resend response", {
        email: email.toLowerCase(),
        status: res.status,
        ok: res.ok,
        bodyOk: data?.ok,
        code: data?.code,
        message: data?.message,
        error: data?.error,
      });

      if (!res.ok || data?.ok === false) {
        const nextError = new Error(data?.error || "Failed to resend.");
        nextError.code = data?.code;
        nextError.status = res.status;
        throw nextError;
      }

      const nextCooldownUntil = Date.now() + RESEND_COOLDOWN_MS;
      writeCooldownUntil(email, nextCooldownUntil);
      setCooldownUntil(nextCooldownUntil);
      setStatus(
        `A new verification email has been sent. Verification links expire in ${VERIFICATION_LINK_TTL_MINUTES} minutes.`
      );
    } catch (e) {
      console.error("[check-email] resend error", e);
      if (e?.code === "TOO_SOON" || e?.status === 429) {
        const existing = readCooldownUntil(email);
        const nextCooldownUntil = existing > Date.now() ? existing : Date.now() + RESEND_COOLDOWN_MS;
        writeCooldownUntil(email, nextCooldownUntil);
        setCooldownUntil(nextCooldownUntil);
        setError(
          `Please wait before resending the verification email${getRemainingSeconds(nextCooldownUntil) > 0 ? ` (${formatCooldown(getRemainingSeconds(nextCooldownUntil))})` : ""}.`
        );
      } else {
        setError(e?.message || "Network error");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="Account">
      <PageHeader />
      <div className="account-content">
        <div className="account-card">
          <h1 className="account-title">Check your email</h1>

          <div className="account-subtitle">
            We sent a verification link to your email. Open it to verify your account.
          </div>

          <div className="account-info" role="note">
            If you do not see it, check spam or junk.
            <br />
            Verification links expire in {VERIFICATION_LINK_TTL_MINUTES} minutes.
          </div>

          {status ? (
            <div className="account-success" role="status">
              {status}
            </div>
          ) : null}

          {error ? (
            <div className="account-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="account-form">
            <label className="account-label">
              Email
              <input
                ref={inputRef}
                type="email"
                value={email}
                readOnly
                className="account-input"
                maxLength={254}
              />
              <div className="account-help-small">
                Need another link? Request a new verification email below.
              </div>
            </label>

            <button
              type="button"
              className="account-btn"
              onClick={handleResend}
              disabled={resendDisabled}
            >
              {sending
                ? "Sending..."
                : remainingSeconds > 0
                  ? `Resend available in ${formatCooldown(remainingSeconds)}`
                  : "Resend verification email"}
            </button>

            <div className="account-signup-findpw" style={{ justifyContent: "center" }}>
              <p className="account-help">
                <Link to="/account" state={{ email }}>
                  Back to Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );
}
